import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { Bug } from '../../interfaces/bug';
import { AdoRepositoryService } from '../../repositories/ado_repository/ado-repository.service';
import { IInterventionEntry } from '../../interfaces/intervention-entry-interface';

interface IPatch {
    op: "replace" | "add" | "remove";
    path: string;
    value: any;
}

@Injectable({
    providedIn: 'root'
})
export class AdoBugsService {

    constructor(
        private adoRepository: AdoRepositoryService
    ) { }

    getAllActiveBugs(): Observable<Bug[]> {
        return this.adoRepository.getAllActiveBugs().pipe(
            map((workItems: any[]) => workItems.map(workItem => {
                const swVersion: string | undefined = workItem.fields["Microsoft.VSTS.Build.FoundIn"];
                const rcpuVersion: string | undefined = workItem.fields["Custom.STMCommit"];
                const latestSwFoundIn: string | undefined = this._keepMajorMinor(this._getLatestFoundInVersion(swVersion, rcpuVersion));
                return {
                    id: workItem.id,
                    title: workItem.fields["System.Title"],
                    description: workItem.fields["System.Description"] || 'No Description',
                    numberOfReports: (Number.isNaN(workItem.fields["Custom.Timesreported"]) || workItem.fields["Custom.Timesreported"] == undefined) ? 0 : workItem.fields["Custom.Timesreported"],
                    totalDowntime: (Number.isNaN(workItem.fields["Custom.TotalDowntime"]) || workItem.fields["Custom.TotalDowntime"] == undefined) ? 0 : workItem.fields["Custom.TotalDowntime"],
                    assigned_to: workItem.fields["System.AssignedTo"]?.displayName ?? "Unassigned",
                    area: workItem.fields["System.AreaPath"] || 'No Area',
                    issueGroup: workItem.fields["Custom.Component"] || 'No Issue Group',
                    customer: workItem.fields["Custom.Customer"] || 'No Customer',
                    hwVersion: workItem.fields["Custom.Version"] || 'No Version',
                    latestSwFoundIn: latestSwFoundIn,
                    fixedInVersion: workItem.fields["Custom.Release"] ? this._keepMajorMinor(workItem.fields["Custom.Release"]) : '',
                } as Bug;
            }))
        );
    }

    updateTotalDowntimeAndNumberOfReports(bugId: number | string, totalDowntime: number, numberOfReports: number) {
        if (!totalDowntime || !numberOfReports || totalDowntime <= 0 || numberOfReports <= 0) {
            console.error(`TotalDowntime and numberOfReports must be larger than 0. Values given was ${totalDowntime}(totalDowntime) and ${numberOfReports}(numberOfReports).`)
            return of(false);
        }
        const patch: IPatch[] = [
            {
                "op": "replace",
                "path": "/fields/Custom.TotalDowntime",
                "value": `${totalDowntime}`
            },
            {
                "op": "replace",
                "path": "/fields/Custom.Timesreported",
                "value": `${numberOfReports}`
            },
        ]
        return this._patchWorkItem(bugId, patch)
    }

    postInterventionAsComment(intervention: IInterventionEntry): Observable<boolean> {
        if (!intervention.BugId) {
            return of(false);
        }

        return this.adoRepository.postComment(intervention.BugId!, this._getInterventionAsComment(intervention)).pipe(
            map(_ => true),
            catchError((err: any) => {
                console.error("Error posting comment to ADO", err);
                return of(false);
            })
        );
    }

    getTitleFromTicketId(ticketId: number | string) { 
        return this.adoRepository.getTitleFromTicketId(ticketId);
    }

    _patchWorkItem(
        ticketId: number | string,
        patch: IPatch[]
    ): Observable<boolean> {
        return this.adoRepository.patch(`wit/workitems/${ticketId}`, patch).pipe(
            map(_ => true),
            catchError((err: any) => {
                console.error("Error trying to update number of reports in ADO", err);
                return of(false);
            })
        );
    }

    _getLatestFoundInVersion(
        roboticsVersion: string | undefined,
        rcpuVersion: string | undefined,
    ): string | undefined {
        if (roboticsVersion && rcpuVersion) {
            return roboticsVersion >= rcpuVersion ? roboticsVersion : rcpuVersion;
        }
        if (roboticsVersion) {
            return roboticsVersion;
        }
        if (rcpuVersion) {
            return rcpuVersion;
        }
        return undefined;
    }

    _keepMajorMinor(version: string | undefined): string | undefined {
        const regex = /^(\d+)\.(\d+)/;
        const match = version?.match(regex);

        if (match) {
            return `${match[1]}.${match[2]}`;
        }
        return version;
    }

    _getInterventionAsComment(intervention: IInterventionEntry): string {
        let comment = `<p>Intervention reported (auto-generated from intervention tracker):</p>`;
        comment += `<ul>`;
        if (intervention.Usecase) {
            comment += `<li>Usecase: ${intervention.Usecase}</li>`;
        }
        comment += `<li>SW version: ${intervention.SwVersion}</li>`;
        comment += `<li>HW version: ${intervention.HwVersion}</li>`;
        comment += `<li>Actions: ${intervention.Actions}</li>`;
        comment += `<li>Description: ${intervention.Description}</li>`;
        if (intervention.Cart) {
            comment += `<li>Cart: ${intervention.Cart}</li>`;
        }
        if (intervention.IssueGroup) {
            comment += `<li>IssueGroup: ${intervention.IssueGroup}</li>`;
        }

        comment += `</ul>`;

        return comment;
    }
}
